/**
 * Hook para manejar la instalación de la PWA
 * Soporta Android (beforeinstallprompt) e iOS (instrucciones manuales)
 */

import { useState, useEffect, useCallback } from 'react';
import logger from '@/lib/logger';

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

interface PWAInstallState {
  isInstallable: boolean;
  isInstalled: boolean;
  isIOS: boolean;
  isAndroid: boolean;
  isStandalone: boolean;
  isMobile: boolean;
}

export function usePWAInstall() {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [state, setState] = useState<PWAInstallState>({
    isInstallable: false,
    isInstalled: false,
    isIOS: false,
    isAndroid: false,
    isStandalone: false,
    isMobile: false,
  });

  useEffect(() => {
    // Detectar plataforma
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isIOS = /iphone|ipad|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isMobile = isIOS || isAndroid || /mobile|tablet/.test(userAgent);

    // Detectar si ya está instalada (modo standalone)
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      || (window.navigator as any).standalone === true
      || document.referrer.includes('android-app://');

    setState(prev => ({
      ...prev,
      isIOS,
      isAndroid,
      isMobile,
      isStandalone,
      isInstalled: isStandalone,
      // En móviles siempre es "instalable" manualmente (si no está instalada)
      isInstallable: isMobile && !isStandalone,
    }));

    // Escuchar evento beforeinstallprompt (Chrome, Edge, Samsung Internet - solo HTTPS)
    const handleBeforeInstall = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
      setState(prev => ({ ...prev, isInstallable: true }));
    };

    // Escuchar cuando se instala la app
    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setState(prev => ({
        ...prev,
        isInstallable: false,
        isInstalled: true
      }));
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = useCallback(async () => {
    if (!installPrompt) {
      return { success: false, platform: 'none' };
    }

    try {
      await installPrompt.prompt();
      const { outcome, platform } = await installPrompt.userChoice;

      if (outcome === 'accepted') {
        setInstallPrompt(null);
        setState(prev => ({ ...prev, isInstallable: false }));
      }

      return { success: outcome === 'accepted', platform };
    } catch (error) {
      logger.error('Error al mostrar prompt de instalación:', error);
      return { success: false, platform: 'error' };
    }
  }, [installPrompt]);

  return {
    ...state,
    promptInstall,
    canPrompt: !!installPrompt,
  };
}
